import React, { useState, useEffect, useContext } from 'react';
import useToastContext from 'hooks/useToastContext';
import { useForm } from 'react-hook-form';
import { apiPost, apiGet } from '../../utils/apiFetch';
import apiPath from '../../utils/apiPath';
import { useTranslation } from 'react-i18next';
import OInputField from 'components/reusable/OInputField';
import formValidation from 'utils/formValidation';
import { useNavigate } from 'react-router-dom';
import AuthContext from 'context/AuthContext';
import ErrorMessage from 'components/ErrorMessage';
import { Editor } from '@tinymce/tinymce-react';
import { isEmpty } from 'lodash';
import OImage from 'components/reusable/OImage';
import OTextArea from 'components/reusable/OTextArea';
import noImageFound from '../../assets/images/No-image-found.jpg';
import Select from 'react-select';

const AddBlog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true
  });
  const notification = useToastContext();
  const [content, setContent] = useState('');
  const [photo, setPhoto] = useState('');
  const [photoError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [postDate, setPostDate] = useState('');
  const { updatePageName } = useContext(AuthContext);

  useEffect(() => {
    updatePageName(t('ADD_BLOG'));
    fetchCategories();
    
    // Set default post date to today
    const today = new Date().toISOString().split('T')[0];
    setPostDate(today);
  }, []);

  const fetchCategories = async () => {
    try {
      const result = await apiGet(apiPath.blogCategory);
      if (result?.data?.success) {
        setCategories(result.data.results.docs);
      }
    } catch (error) {
      console.log('Error fetching categories:', error);
    }
  };

  // Image upload handler for TinyMCE
  const handleImageUpload = (blobInfo, progress) => {
    return new Promise(async (resolve, reject) => {
      try {
        setUploadingImage(true);
        
        const formData = new FormData();
        formData.append('image', blobInfo.blob(), blobInfo.filename());

        console.log('Uploading image:', blobInfo.filename());

        const response = await apiPost(apiPath.uploadImage, formData);
        
        console.log('Upload response:', response);

        if (response?.data?.success) {
          const imageUrl = response.data.results?.imageUrl;
          if (imageUrl) {
            console.log('Image uploaded successfully. URL:', imageUrl);
            resolve(imageUrl);
          } else {
            reject('Upload successful but no image URL in response');
          }
        } else {
          const errorMsg = response?.data?.message || 'Upload failed on server';
          console.error('Server rejected upload:', errorMsg);
          reject(errorMsg);
        }
      } catch (error) {
        console.error('Image upload catch error:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Upload failed';
        reject('Image upload failed: ' + errorMsg);
      } finally {
        setUploadingImage(false);
      }
    });
  };

  const onSubmit = async data => {
    try {
      if (!isEmpty(content)) {
        const formData = new FormData();
        formData.append('title', data?.title?.trim());
        formData.append('writer_name', data?.writer_name);
        formData.append('metaTitle', data?.metaTitle);
        formData.append('metaDescription', data?.metaDescription);
        formData.append('keywords', data?.keywords);
        formData.append('description', content);
        formData.append('post_date', postDate ? new Date(postDate).toISOString() : new Date().toISOString());
        
        // Append each category individually
        selectedCategories.forEach(category => {
          formData.append('categories', category.value);
        });
        
        if (photo) {
          formData.append('blogImage', photo);
        }
        
        console.log('Submitting blog form...');
        
        const res = await apiPost(apiPath.blog, formData);
        if (res?.data?.success) {
          navigate('/blog-management');
          notification.success(res.data?.message);
        } else {
          notification.error(res?.data?.message);
        }
      } else {
        notification.error('Please enter description');
      }
    } catch (err) {
      console.log('err:', err);
      notification.error('Error adding blog: ' + err.message);
    }
  };

  const handleCategoryChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedCategories(selected);
  };

  return (
    <>
      <div>
        <div className='bg-[#F9F9F9]'>
          <div className='px-3 py-4'>
            <div className='bg-white border border-[#E9EDF9] rounded-lg'>
              <div className='border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4'>
                <div className='col-span-3 flex flex-wrap items-center' />
              </div>

              <div className='flex pl-5 pt-4'>
                <strong>{t('O_ADMIN_BLOG_IMAGE_SIZE_SUGGESTION')}</strong>
              </div>

              <div className='pl-5 pt-4 flex'>
                <label className='mb-2 block pr-1'>
                  <strong>{t('UPLOAD_IMAGE')}</strong>
                </label>
                <input
                  type='file'
                  name='photo'
                  placeholder='Photo'
                  className='form-control'
                  accept='image/png,image/jpeg,image/jpg'
                  onChange={event => {
                    setPhoto(event?.target?.files[0]);
                  }}
                />
              </div>

              {photoError && <ErrorMessage message={photoError} />}

              <div className='pl-5 pt-4' style={{width:"300px"}}>
                <OImage
                  src={
                    typeof photo === 'object'
                      ? URL.createObjectURL(photo)
                      : photo
                  }
                  fallbackUrl={noImageFound}
                />
              </div>

              <div className='p-5 shadow-md'>
                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='title'
                  inputLabel={
                    <>
                      {t('TITLE')}
                      <span className='text-red-500'>*</span>
                    </>
                  }
                  type='text'
                  autoFocus
                  register={register('title', formValidation['title'])}
                  errors={errors}
                />

                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='writer_name'
                  inputLabel={<>{t('WRITER_NAME')}</>}
                  type='text'
                  register={register('writer_name')}
                />

                {/* Post Date Field */}
                <div className='relative z-0 mb-6 w-full group'>
                  <label className='block mb-2 text-sm font-medium text-gray-900'>
                    {t('POST_DATE')} <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='date'
                    value={postDate}
                    onChange={(e) => setPostDate(e.target.value)}
                    className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
                    required
                  />
                </div>

                <div className='relative z-10 mb-6 w-full group'>
                  <label className='block mb-2 text-sm font-medium text-gray-900'>
                    {t('CATEGORIES')}
                  </label>
                  <Select
                    isMulti
                    name='categories'
                    options={categories.map((category) => ({
                      value: category._id,
                      label: category.name,
                    }))}
                    value={selectedCategories}
                    onChange={(selectedOptions) => {
                      // Store selected options in state
                      setSelectedCategories(selectedOptions || []);
                    }}
                    className='basic-multi-select'
                    classNamePrefix='select'
                  />
                </div>

                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='metaTitle'
                  inputLabel={<>{t('META_TITLE')}</>}
                  type='text'
                  register={register('metaTitle', formValidation['metaTitle'])}
                />

                <OTextArea
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='metaDescription'
                  inputLabel={<>{t('META_DESCRIPTION')}</>}
                  type='text'
                  register={register(
                    'metaDescription',
                    formValidation['metaDescription']
                  )}
                />

                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='keywords'
                  inputLabel={<>{t('KEYWORDS')}</>}
                  type='text'
                  register={register('keywords', formValidation['keywords'])}
                />

                <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px' }}>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Blog Content {uploadingImage && <span className="text-blue-500 text-sm">(Uploading image...)</span>}
                    </label>
                  </div>
                  
                  <Editor
                    apiKey="mkzldhxz903im4nozx302n82dfa451insn0l8g423ay9x8s0"
                    value={content}
                    onEditorChange={(newValue) => setContent(newValue)}
                    init={{
                      height: 500,
                      menubar: 'file edit view insert format tools help',
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'paste', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | formatselect | bold italic backcolor | ' +
                        'alignleft aligncenter alignright alignjustify | ' +
                        'link image | bullist numlist outdent indent | removeformat | code | help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                      
                      // Image upload configuration
                      images_upload_handler: handleImageUpload,
                      automatic_uploads: true,
                      paste_data_images: false,
                      
                      // File picker configuration
                      file_picker_types: 'image',
                      file_picker_callback: (callback, value, meta) => {
                        const input = document.createElement('input');
                        input.setAttribute('type', 'file');
                        input.setAttribute('accept', 'image/*');
                        
                        input.onchange = function() {
                          const file = this.files[0];
                          if (file) {
                            const blobInfo = {
                              blob: () => file,
                              filename: () => file.name
                            };
                            
                            handleImageUpload(blobInfo, () => {})
                              .then(imageUrl => {
                                callback(imageUrl, { alt: file.name });
                              })
                              .catch(error => {
                                console.error('File picker upload failed:', error);
                                notification.error('Failed to upload image');
                              });
                          }
                        };
                        
                        input.click();
                      },
                      
                      // Prevent direct pasting of images
                      paste_preprocess: (plugin, args) => {
                        const html = args.content;
                        if (html.includes('<img')) {
                          notification.error('Please use the image upload button to add images instead of pasting.');
                          args.content = '';
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='border-slate-200 rounded-b flex items-center justify-end p-6 border-t border-solid'>
        <button
          className='rounded mr-6 ease-linear transition-all text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none duration-150'
          onClick={() => navigate('/blog-management')}
          type='button'
        >
          {t('O_BACK')}
        </button>

        <button
          onClick={handleSubmit(onSubmit)}
          className='outline-none bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg focus:outline-none mr-1 ease-linear transition-all duration-150'
          type='submit'
        >
          {t('O_ADD')}
        </button>
      </div>
    </>
  );
};

export default AddBlog;